import { useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, Mail, ChevronDown, ChevronUp, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";
import { FEATURE_CREDITS } from "@/lib/constants/costs";
import { MONEY_BACK_DAYS, WELCOME_CREDITS } from "@/lib/constants/tiers";

const faqs = [
  {
    question: "What are credits and how do they work?",
    answer: `Credits are the currency used to generate AI content on NimbleNode. Each AI generation action (image, video, audio) costs a certain number of credits depending on the model and settings used. Credits never expire. You receive ${WELCOME_CREDITS} welcome credits on signup, and can purchase more at any time from the Credits page.`
  },
  {
    question: "How many credits does image generation cost?",
    answer: `Image generation costs ${FEATURE_CREDITS['image generation with gemini-flash-image']} credits for the Flash model and ${FEATURE_CREDITS['image generation with gemini-pro-image']} credits for the Pro model. Video generation costs vary by duration and quality. You can always see the credit cost before confirming a generation.`
  },
  {
    question: "Why did my image generation fail?",
    answer: "Generation can fail for a few reasons: your prompt may have triggered safety filters (try rephrasing it), you may not have enough credits, or there may be a temporary API issue. Credits are automatically refunded if a generation fails. If the problem persists, please contact support."
  },
  {
    question: "How does social media publishing work?",
    answer: "NimbleNode uses Ayrshare to publish to 20+ social platforms. You need to connect your social accounts via the Social Hub page. Once connected, you can publish content directly from the platform or schedule it using the Content Calendar tab."
  },
  {
    question: "How do I connect my social media accounts?",
    answer: "Go to Social Hub in the left navigation, then click 'Connect Accounts'. This will open the Ayrshare account connection page where you can link Instagram, Facebook, Twitter/X, LinkedIn, TikTok, YouTube, Pinterest, and Threads."
  },
  {
    question: "Can I cancel my subscription?",
    answer: `Yes, you can cancel at any time. Your access continues until the end of your billing period. We offer a ${MONEY_BACK_DAYS}-day money-back guarantee on all new subscriptions — contact support@nimblenode.io within ${MONEY_BACK_DAYS} days of your first payment for a full refund.`
  },
  {
    question: "What happens to my credits if I cancel?",
    answer: "Monthly credits expire at the end of each billing cycle. Top-up credits (purchased separately) never expire and remain in your account even after cancellation."
  },
  {
    question: "How do I use the Design Editor?",
    answer: "The Design Studio (accessible from the left nav) is a full design editor powered by Polotno. You can create graphics from templates, customize sizes for different platforms, add text and shapes, and export as PNG, JPEG, PDF, or MP4."
  },
  {
    question: "Is my data safe?",
    answer: "Yes. We use industry-standard encryption for all data in transit and at rest. We do not sell your personal data. Please review our Privacy Policy for full details on how we handle your data."
  },
  {
    question: "How do I request a refund?",
    answer: "Email support@nimblenode.io with your account email and reason. Refund requests for subscriptions within the 14-day guarantee window are processed within 3-5 business days. Credit top-up purchases are generally non-refundable, but we handle cases individually."
  },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#334155] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/5 transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-inset"
      >
        <span className="text-white font-medium">{question}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#94A3B8] flex-shrink-0" aria-hidden="true" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8] flex-shrink-0" aria-hidden="true" />}
      </button>
      {open && (
        <div className="px-5 pb-4 text-[#94A3B8] text-sm leading-relaxed border-t border-[#334155] pt-3">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    try {
      await base44.integrations.Core.SendEmail({
        to: "support@nimblenode.io",
        subject: `Support Request from ${form.name}`,
        body: `Name: ${form.name}\nEmail: ${form.email}\n\nMessage:\n${form.message}`
      });
      setSent(true);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send your message. Please try again or email support@nimblenode.io directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-7 h-7 text-cyan-400" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Help & Support</h1>
          <p className="text-[#94A3B8]">Find answers or get in touch with our team.</p>
        </div>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-white mb-6">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-2">Contact Support</h2>
          <p className="text-[#94A3B8] mb-6">Can't find an answer? Send us a message and we'll respond within 24 hours.</p>

          {sent ? (
            <div className="p-6 rounded-xl border border-cyan-500/30 bg-cyan-500/5 text-center" role="status" aria-live="polite">
              <p className="text-cyan-300 font-medium">✓ Message sent successfully!</p>
              <p className="text-[#94A3B8] text-sm mt-1">We'll get back to you at {form.email || "your email"} within 24 hours.</p>
              <Button onClick={() => setSent(false)} variant="outline" className="mt-4 border-white/20 text-white hover:bg-white/10">Send Another</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 p-6 rounded-xl border border-[#334155] bg-[#1E293B]/50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="support-name" className="text-sm text-[#94A3B8] mb-1 block">Name</label>
                  <Input
                    id="support-name"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className="bg-white/5 border-white/20 text-white placeholder:text-[#475569]"
                  />
                </div>
                <div>
                  <label htmlFor="support-email" className="text-sm text-[#94A3B8] mb-1 block">Email</label>
                  <Input
                    id="support-email"
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-white/5 border-white/20 text-white placeholder:text-[#475569]"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="support-message" className="text-sm text-[#94A3B8] mb-1 block">Message</label>
                <Textarea
                  id="support-message"
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  placeholder="Describe your issue or question..."
                  rows={5}
                  className="bg-white/5 border-white/20 text-white placeholder:text-[#475569] resize-none"
                />
              </div>
              <Button type="submit" disabled={sending} className="bg-cyan-600 hover:bg-cyan-700 text-white">
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Message</>}
              </Button>
            </form>
          )}
        </section>

        {/* Direct Email */}
        <div className="p-5 rounded-xl border border-[#334155] bg-[#1E293B]/30 flex items-center gap-4 mb-12">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <p className="text-white font-medium">Email us directly</p>
            <a href="mailto:support@nimblenode.io" className="text-cyan-400 hover:underline text-sm">support@nimblenode.io</a>
          </div>
        </div>

        {/* Footer Links */}
        <div className="pt-8 border-t border-[#334155] flex flex-wrap gap-4 justify-center text-sm text-[#94A3B8]">
          <Link to="/" className="hover:text-cyan-400 transition-colors">← Back to Home</Link>
          <Link to="/TermsOfService" className="hover:text-cyan-400 transition-colors">Terms of Service</Link>
          <Link to="/PrivacyPolicy" className="hover:text-cyan-400 transition-colors">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}